import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { NoteImage } from "@/components/mt/NoteImage";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory1() {
  const { lesson, course, accent } = findLesson("music-theory", "1");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT1-Major-Scale-Animated.mp4"
        caption="The Major Scale — W-W-H-W-W-W-H, built on a keyboard"
        hint="≈ 33 sec · watch first"
      />
      <Chapter number="01" eyebrow="Music Theory · Chapter One" title="What theory is, and what the major scale is for">
        <p>
          Theory isn't notation. Reading and writing music — staves, clefs, time signatures —
          is a separate skill (the staff-notation course handles that). Theory is something
          quieter and older: the <em>unwritten laws</em> that govern how music holds together.
          Centuries of musicians documenting what works, why it works, and why some
          combinations of notes feel like home while others feel like a question.
        </p>
        <MTQuote>
          Theory is just a great documentation of music over the ages, and we have to
          respect music from all generations, all cultures. So, what theory tries to do and
          has always been trying to do historically is to preserve our art form and drive it
          forward to the next level.
        </MTQuote>
        <p>
          You don't need theory to play one or two songs. You need theory the moment you want
          to <em>collaborate</em> — to call out a key with a guitarist, transpose a melody for
          a singer, or improvise over a chord progression you've never seen before. Guessing
          on the piano gets you into the room. Theory keeps you there.
        </p>
        <p>
          And among every topic theory covers, almost everyone agrees the most important
          starting point is the <strong>scale</strong>. So we'll start with the most popular
          one: the <strong>major scale</strong>.
        </p>
      </Chapter>

      <Chapter eyebrow="The 12 notes" title="Music has only twelve. Then it loops.">
        <Prose>
          <p>
            Before we can build any scale, we need to lay out the alphabet. There are
            <strong> only 12 notes</strong> in Western music — and the piano keyboard is laid
            out so you can see them at a glance. Walk up the keys one at a time, mixing
            white and black, and you cycle through 12 unique pitches before the names start
            repeating.
          </p>
          <p>
            Musicians lay these twelve out on two different clocks. Walk them in order and
            you get the <em>chromatic</em> clock — twelve positions, each one semitone
            apart. Re-order them so that every step is a <em>perfect fifth</em> and you get
            the circle of fifths below, which is the one that makes key relationships jump
            out. Same twelve notes; two different orders:
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths size={360} />
        </div>

        <NoteImage
          src="/notes/music-theory/finals/01a-twins-and-triplets.png"
          caption="Jason's note: the 12 notes laid out around the keyboard. 7 naturals (A B C D E F G) plus 5 sharps/flats arranged as 'twins' (C♯/D♭, D♯/E♭) and 'triplets' (F♯/G♭, G♯/A♭, A♯/B♭). The black-key cluster pattern is your visual anchor."
        />

        <Prose>
          <p>
            Of those twelve, <strong>seven are "natural"</strong> (the white keys: A B C D
            E F G) and <strong>five are sharps or flats</strong> (the black keys). Two
            quirks of the keyboard are worth burning into memory:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>There is <strong>no black key between B and C</strong>.</li>
            <li>There is <strong>no black key between E and F</strong>.</li>
          </ul>
          <p>
            Every black key has two names — its <strong>sharp</strong> spelling and its
            <strong> flat</strong> spelling. C♯ and D♭ are the same key. F♯ and G♭ are the
            same key. The choice between names becomes important when you start writing
            scales — and it follows one rule, which we're about to meet.
          </p>
        </Prose>

        <NoteImage
          src="/notes/music-theory/finals/01b-order-of-sharps-flats.png"
          caption="Reading sharps and flats off the keyboard pattern itself. Twins/triplets pattern read left→right gives the order of sharps (F♯ C♯ G♯ D♯ A♯). Read right→left it gives the order of flats (B♭ E♭ A♭ D♭ G♭). One picture; two pieces of memory work, free."
        />
      </Chapter>

      <Chapter eyebrow="Why we narrow 12 down to 7" title="A scale is a usable subset">
        <Prose>
          <p>
            Twelve notes in a row is a lot. If you simply played them all, in order, you'd
            get the <em>chromatic scale</em> — and your ear would struggle. There's no
            anchor, no leader, nothing to come back to. Just a smooth slide of pitches with
            no character.
          </p>
          <p>
            That's why almost every scale in popular music — major, minor, modes, blues —
            picks <strong>seven notes</strong> out of the twelve. Seven is the magic number:
            enough to give the scale a flavour, few enough to keep a clear sense of "home".
            (The other common subset is five, the pentatonic — we'll meet that in Chapter
            3.)
          </p>
          <p>
            To <em>pick</em> seven notes from twelve, we need a recipe. The recipe uses two
            simple movements:
          </p>
        </Prose>

        <Glossary>
          <Term name="Tone (whole-step)" def="A two-semitone move. From C → D, you skip C♯ in between." />
          <Term name="Semitone (half-step)" def="A one-semitone move. From C → C♯, no skipping." />
          <Term name="Chromatic" def="From the Greek chroma, 'colour'. Used loosely to mean 'using all twelve' — as in chromatic scale." />
          <Term name="Diatonic" def="Greek for 'through the tones'. Used to describe scales (or chords) that stay inside the seven notes of a key." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="The recipe" title="The major-scale formula: T T S · T T T S">
        <Prose>
          <p>
            Every major scale follows the same seven-step recipe — measured in tones (T)
            and semitones (S):
          </p>
        </Prose>

        <TheoryBox label="The major scale formula">
          <div className="text-center font-mt text-2xl text-amber-200">
            <strong>T &nbsp;·&nbsp; T &nbsp;·&nbsp; S &nbsp;·&nbsp; T &nbsp;·&nbsp; T &nbsp;·&nbsp; T &nbsp;·&nbsp; S</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (whole-whole-half-whole-whole-whole-half · same recipe in any key)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            That's it. Pick a starting note. Apply the recipe. The seven notes that fall out
            are the major scale on that root. Let's walk through <strong>A major</strong>:
          </p>
        </Prose>

        <Steps>
          <Step><strong>1 (Sa) — A.</strong> Pick the root.</Step>
          <Step><strong>2 (Re) — B.</strong> Two semitones up from A, skipping A♯. <em>Tone.</em></Step>
          <Step><strong>3 (Ga) — C♯.</strong> Two semitones up from B (B → C → C♯). <em>Tone.</em></Step>
          <Step><strong>4 (Ma) — D.</strong> One semitone up from C♯. <em>Semitone.</em></Step>
          <Step><strong>5 (Pa) — E.</strong> Two semitones up from D. <em>Tone.</em></Step>
          <Step><strong>6 (Dha) — F♯.</strong> Two semitones up from E. <em>Tone.</em></Step>
          <Step><strong>7 (Ni) — G♯.</strong> Two semitones up from F♯. <em>Tone.</em></Step>
          <Step><strong>8 (Sa') — A.</strong> One semitone up from G♯ closes the octave. <em>Semitone.</em></Step>
        </Steps>

        <Prose>
          <p>
            That's <strong>A major</strong>: <span className="font-mono text-amber-300">A B
            C♯ D E F♯ G♯</span>. Three sharps. Use the explorer below to hear it — and to
            transpose the same recipe to any other key:
          </p>
        </Prose>

        <ScaleExplorer tonic="A" scales={["major"]} />

        <NoteImage
          src="/notes/music-theory/finals/01c-major-scale-formula-A-major.png"
          caption="Jason's note: the major-scale formula laid out from I to VIII with the Indian swaras (sa re ga ma pa dha ni sa') beneath the Roman numerals. Below it, A major and B major spelled out — every letter A through G appearing exactly once."
        />

        <Prose>
          <p>
            Click any tonic button above to rebuild the scale instantly. The recipe never
            changes — only the seven notes that come out the other end.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The naming rule" title="Every letter once. No letter twice.">
        <Prose>
          <p>
            One subtle but important rule: when you write a major scale, you must use
            <strong> every letter A-G exactly once</strong>. Never skip a letter; never repeat
            one. That's why A major is spelled with C♯, not D♭ — even though they're the
            same key on the piano.
          </p>
          <p>
            If you wrote A major as <span className="font-mono">A B D♭ D E F♯ G♯</span>, you'd
            have <em>two</em> D's and no C. Music notation breaks. Sheet music becomes
            unreadable. The scale gets the wrong "shape" on the staff.
          </p>
          <p>
            So: A B C♯ D E F♯ G♯, not A B D♭ D E F♯ G♯. The note is the same; the spelling
            tells the truth about the scale.
          </p>
        </Prose>

        <TheoryBox label="The seven-letter rule">
          When you write a scale, you must use every letter A through G exactly once.
          That's why F♯ exists in A major (not G♭) — and why B♭ exists in F major (not A♯).
          The note on the piano is identical; the <em>name</em> tells the story of how the
          scale is built.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Try it yourself" title="Build B major and C major in your head">
        <Prose>
          <p>
            Apply the same TTSTTTS recipe starting from B. Two steps up from B is C♯. Two
            more is D♯. One step is E. Two steps is F♯. Two is G♯. Two is A♯. One is B.
          </p>
          <p>
            <span className="font-mono text-amber-300">B major: B C♯ D♯ E F♯ G♯ A♯</span> — five
            sharps. The two scales overlap in five of their seven notes; the differences are
            A♯ vs A, and D♯ vs D. Notice the recipe doesn't care.
          </p>
          <p>
            And <strong>C major</strong>? Walk it: T to D, T to E, S to F, T to G, T to A,
            T to B, S to C. <span className="font-mono text-amber-300">C D E F G A B</span> —
            zero sharps, zero flats. C major is the only major scale that uses just the
            white keys. That's why it's often taught first; it's the simplest spelling, not
            the simplest <em>sound</em>.
          </p>
        </Prose>

        <ScaleExplorer tonic="C" scales={["major"]} />

        <NoteImage
          src="/notes/music-theory/finals/01d-forming-A-and-Bb-major-step-by-step.png"
          caption="The 3-step trick for any major scale. Step 1: write the seven letters starting on your tonic. Step 2: count sharps/flats from the key signature. Step 3: pencil them onto the right letters. A major = 3 sharps (F♯ C♯ G♯). B♭ major = 2 flats (B♭ E♭)."
        />
      </Chapter>

      <Chapter eyebrow="What's next" title="Twelve scales · twelve homes">
        <Prose>
          <p>
            There are twelve possible roots — so there are twelve major scales. The recipe
            stays the same; only the starting pitch shifts. Once the formula is automatic,
            you stop "memorising scales" and start <em>generating</em> them on demand.
          </p>
          <p>
            Your homework for this chapter: <strong>write out all twelve major scales</strong>.
            Use the seven-letter rule. Then play each one on the keyboard and listen for the
            pattern. Once they're in your hand and your ear, the rest of this
            course unfolds easily — every chord, every progression, every modulation rests on
            this one foundation.
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/lesson/music-theory-2"
        label="Chapter 2 · Scale Degrees & Building Melodies"
        why="With the major scale built, the next move is to number its notes — 1, 2, 3, 4, 5, 6, 7 — and use those numbers to compose and transpose any melody."
      />
    </LessonShell>
  );
}
